import React from 'react';
import styled from 'styled-components';
import { Card } from './StyledComponents';
import { CONTRACT_GROUPS, CONFIG_LEDGER, addrUrl, txUrl, sourcifyUrl } from '../utils/provenance';

// ---------------------------------------------------------------------------
// Contracts / transparency tab. Every contract and every configuration change
// is a real on-chain fact with a verifiable link. Read without a wallet.
// ---------------------------------------------------------------------------

const Wrap = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Intro = styled.div`
  text-align: center;
  padding: 1rem 0 0.5rem;
  h2 { color: #ffd700; margin: 0 0 0.5rem; }
  p { color: #ccc; max-width: 680px; margin: 0 auto; line-height: 1.6; }
`;

const GroupTitle = styled.h3`
  color: #ffd700;
  margin: 0 0 0.25rem;
  font-size: 1.15rem;
`;
const GroupBlurb = styled.p`
  color: #999;
  font-size: 0.85rem;
  margin: 0 0 1rem;
  line-height: 1.5;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  align-items: start;
  padding: 0.9rem 0;
  border-top: 1px solid #2a2a2a;

  &:first-of-type { border-top: none; }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const Name = styled.div`
  .n { font-weight: 600; color: #fff; }
  .r { font-size: 0.82rem; color: #999; margin-top: 0.15rem; line-height: 1.4; }
  .note { font-size: 0.78rem; color: #10b981; margin-top: 0.3rem; }
  .addr {
    font-family: monospace;
    font-size: 0.75rem;
    color: #777;
    margin-top: 0.35rem;
    word-break: break-all;
  }
`;

const Links = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  justify-content: flex-end;
  @media (max-width: 620px) { justify-content: flex-start; }
`;

const Pill = styled.a`
  font-size: 0.75rem;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  text-decoration: none;
  white-space: nowrap;
  border: 1px solid ${(p) => (p.$kind === 'verified' ? 'rgba(16,185,129,0.5)' : '#444')};
  color: ${(p) => (p.$kind === 'verified' ? '#10b981' : '#ffd700')};
  background: ${(p) => (p.$kind === 'verified' ? 'rgba(16,185,129,0.08)' : 'transparent')};

  &:hover { border-color: #ffd700; }
`;

const Badge = styled.span`
  font-size: 0.72rem;
  padding: 0.3rem 0.6rem;
  border-radius: 999px;
  border: 1px solid #555;
  color: #aaa;
  white-space: nowrap;
`;

const LedgerItem = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  align-items: center;
  padding: 0.55rem 0;
  border-top: 1px solid #222;
  font-size: 0.88rem;
  color: #ddd;

  &:first-of-type { border-top: none; }
  a { color: #ffd700; text-decoration: none; font-size: 0.78rem; white-space: nowrap; }
  a:hover { text-decoration: underline; }
`;

const ContractsPage = () => (
  <Wrap>
    <Intro>
      <h2>Contracts &amp; configuration</h2>
      <p>
        JDAI runs on MakerDAO&apos;s audited contracts, unchanged. Every contract below is
        verified on Sourcify, and every configuration change is a public transaction you
        can open and read. Don&apos;t trust this page — verify it.
      </p>
    </Intro>

    {CONTRACT_GROUPS.map((g) => (
      <Card key={g.group}>
        <GroupTitle>{g.group}</GroupTitle>
        <GroupBlurb>{g.blurb}</GroupBlurb>
        {g.contracts.map((c) => (
          <Row key={c.address}>
            <Name>
              <div className="n">{c.name}</div>
              <div className="r">{c.role}</div>
              {c.note && <div className="note">✓ {c.note}</div>}
              <div className="addr">{c.address}</div>
            </Name>
            <Links>
              {c.verified
                ? <Pill $kind="verified" href={sourcifyUrl(c.address)} target="_blank" rel="noopener noreferrer">✓ Verified source ↗</Pill>
                : c.reused
                  ? <Badge>Reused from Ethereum fork</Badge>
                  : null}
              <Pill href={addrUrl(c.address)} target="_blank" rel="noopener noreferrer">Explorer ↗</Pill>
              {c.deployTx && <Pill href={txUrl(c.deployTx)} target="_blank" rel="noopener noreferrer">Deploy tx ↗</Pill>}
            </Links>
          </Row>
        ))}
      </Card>
    ))}

    <Card>
      <GroupTitle>Configuration ledger</GroupTitle>
      <GroupBlurb>
        Every setting the system was configured with, as the on-chain transaction that set
        it. Nothing was configured off the record.
      </GroupBlurb>
      {CONFIG_LEDGER.map((section) => (
        <div key={section.title} style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem' }}>
            <strong style={{ color: '#ffd700', fontSize: '0.95rem' }}>{section.title}</strong>
            <span style={{ fontSize: '0.75rem', color: '#777' }}>{section.date}</span>
          </div>
          {section.note && (
            <p style={{ margin: '0 0 0.6rem', fontSize: '0.8rem', color: '#10b981', lineHeight: 1.5 }}>
              {section.note}
            </p>
          )}
          {section.items.map((it) => (
            <LedgerItem key={it.tx}>
              <span>{it.what}</span>
              <a href={txUrl(it.tx)} target="_blank" rel="noopener noreferrer">View transaction ↗</a>
            </LedgerItem>
          ))}
        </div>
      ))}
    </Card>

    <p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#777', paddingBottom: '1rem' }}>
      This page lists only contracts that are live on-chain. Modules still in development
      (such as automated liquidations) appear here once deployed and verified — never before.
    </p>
  </Wrap>
);

export default ContractsPage;
